/**
 * Sign-in. Production signs in with Atlas (auth.repu.me), the sign-in for all
 * of Repu, when the API advertises it (`GET /v1/auth/config`). Otherwise, in
 * local development, Firebase Authentication with the Auth emulator: its web
 * config comes from `/__/firebase/init.json`, or the emulator project.
 */
import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  GithubAuthProvider,
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  getRedirectResult,
  signInWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from "firebase/auth";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { beginSignIn, CALLBACK_PATH, completeSignIn, loadSession, refreshSession, saveSession, signOutUrl, type AtlasConfig, type AtlasSession } from "./atlas.js";

/** The signed-in person, whichever way they signed in. */
export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

interface AuthState {
  user: AuthUser | null;
  /** "atlas" in production; "firebase" for local development with the emulator. */
  provider: "atlas" | "firebase" | null;
  ready: boolean;
  error: string | null;
  /** True when connected to the Firebase Auth emulator (local development). */
  emulator: boolean;
  signIn(): Promise<void>;
  /** Emulator only: signs in (creating if needed) an email/password test account. */
  signInTestAccount(email: string, name: string): Promise<void>;
  signOut(): Promise<void>;
  token(): Promise<string>;
}

const AuthContext = createContext<AuthState | null>(null);

const isLocal = (): boolean => ["localhost", "127.0.0.1"].includes(window.location.hostname);

async function loadConfig(): Promise<Record<string, string>> {
  try {
    const res = await fetch("/__/firebase/init.json");
    if (res.ok && res.headers.get("content-type")?.includes("json")) return (await res.json()) as Record<string, string>;
  } catch {
    /* not served by Firebase Hosting */
  }
  const env = import.meta.env;
  if (typeof env["VITE_FIREBASE_API_KEY"] === "string") {
    return {
      apiKey: env["VITE_FIREBASE_API_KEY"],
      authDomain: String(env["VITE_FIREBASE_AUTH_DOMAIN"] ?? ""),
      projectId: String(env["VITE_FIREBASE_PROJECT_ID"] ?? ""),
    };
  }
  return { apiKey: "fake-api-key", authDomain: "localhost", projectId: "demo-repu-ci" };
}

let firebase: Promise<{ app: FirebaseApp; auth: Auth; emulator: boolean }> | undefined;

function initFirebase(): Promise<{ app: FirebaseApp; auth: Auth; emulator: boolean }> {
  firebase ??= loadConfig().then(async (config) => {
    const app = initializeApp(config);
    const auth = getAuth(app);
    const emulator = isLocal() && (config["projectId"]?.startsWith("demo-") ?? false);
    if (emulator) {
      connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
      await getRedirectResult(auth).catch((e: unknown) => { console.warn("sign-in redirect failed", e); return null; });
    }
    return { app, auth, emulator };
  });
  return firebase;
}

async function loadAtlasConfig(): Promise<AtlasConfig | null> {
  try {
    const res = await fetch("/api/v1/auth/config");
    if (!res.ok) return null;
    const body = (await res.json()) as { atlas?: { issuer: string; webClientId: string } | null };
    return body.atlas ?? null;
  } catch {
    return null;
  }
}

function fromFirebase(u: User | null): AuthUser | null {
  return u ? { uid: u.uid, email: u.email, displayName: u.displayName, photoURL: u.photoURL } : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [emulator, setEmulator] = useState(false);
  const [atlas, setAtlas] = useState<AtlasConfig | null>(null);
  const session = useRef<AtlasSession | null>(null);
  const refreshing = useRef<Promise<AtlasSession> | null>(null);

  useEffect(() => {
    let unsubscribe: () => void = () => undefined;
    void (async () => {
      const config = await loadAtlasConfig();
      if (config) {
        setAtlas(config);
        try {
          if (window.location.pathname === CALLBACK_PATH) {
            const done = await completeSignIn(config, window.location.search);
            saveSession(done.session);
            session.current = done.session;
            window.history.replaceState(null, "", done.returnTo);
          } else {
            session.current = loadSession();
          }
          setUser(session.current ? { ...session.current.user, photoURL: null } : null);
        } catch (e) {
          window.history.replaceState(null, "", "/");
          setError(e instanceof Error ? e.message : String(e));
        }
        setReady(true);
        return;
      }
      try {
        const { auth: a, emulator: e } = await initFirebase();
        setAuth(a);
        setEmulator(e);
        unsubscribe = onAuthStateChanged(a, (u) => {
          setUser(fromFirebase(u));
          setReady(true);
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setReady(true);
      }
    })();
    return () => {
      unsubscribe();
    };
  }, []);

  const state: AuthState = {
    user,
    provider: atlas ? "atlas" : auth ? "firebase" : null,
    ready,
    error,
    emulator,
    async signInTestAccount(email, name) {
      if (!auth || !emulator) return;
      setError(null);
      const password = "emulator-test-account";
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch {
        try {
          const created = await createUserWithEmailAndPassword(auth, email, password);
          await updateProfile(created.user, { displayName: name });
          await created.user.getIdToken(true);
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    },
    async signIn() {
      setError(null);
      if (atlas) {
        await beginSignIn(atlas, window.location.pathname + window.location.search);
        return;
      }
      if (!auth) return;
      try {
        // The emulator's sign-in widget uses the redirect flow (works in embedded browsers).
        if (emulator) await signInWithRedirect(auth, new GithubAuthProvider());
        else await signInWithPopup(auth, new GithubAuthProvider());
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    async signOut() {
      if (atlas) {
        saveSession(null);
        session.current = null;
        window.location.assign(signOutUrl(atlas));
        return;
      }
      if (auth) await firebaseSignOut(auth);
    },
    async token() {
      if (atlas) {
        const current = session.current;
        if (!current) throw new Error("Not signed in");
        if (current.expiresAt - Date.now() > 60_000) return current.accessToken;
        refreshing.current ??= refreshSession(atlas, current).finally(() => {
          refreshing.current = null;
        });
        try {
          const next = await refreshing.current;
          saveSession(next);
          session.current = next;
          return next.accessToken;
        } catch {
          // Signed out elsewhere, suspended, or the refresh token expired: start again.
          saveSession(null);
          session.current = null;
          setUser(null);
          throw new Error("Your sign-in has ended. Sign in again.");
        }
      }
      if (!auth?.currentUser) throw new Error("Not signed in");
      return auth.currentUser.getIdToken();
    },
  };
  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
